import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import api from '../utils/api';

const UserDataContext = createContext();

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within UserDataProvider');
  }
  return context;
};

const DEFAULT_USAGE = {
  storageUsed: 0,
  storageLimit: 0,
  documentsUsed: 0,
  documentsLimit: 0,
  projectsUsed: 0,
  projectsLimit: 0,
};

const DEFAULT_TOKEN_USAGE = {
  dailyUsed: 0,
  dailyLimit: 0,
  weeklyUsed: 0,
  weeklyLimit: 0,
  todayUsage: 0,
  weekUsage: 0,
  monthUsage: 0,
};

export const UserDataProvider = ({ children }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [orgInfo, setOrgInfo] = useState({ company: '', phone: '', location: '' });
  const [pricingData, setPricingData] = useState(null);
  const [dataUsage, setDataUsage] = useState(DEFAULT_USAGE);
  const [tokenUsage, setTokenUsage] = useState(DEFAULT_TOKEN_USAGE);

  // Single source of truth for fetching and applying user data.
  // Previously duplicated between the initial useEffect and refresh(), which
  // drifted over time. Now both paths share the same implementation.
  const loadUserData = useCallback(async () => {
    setLoading(true);
    try {
      const [profileResponse, dashboardResponse, accountResponse] = await Promise.all([
        api.get('/users/me'),
        api.get('/storage/dashboard'),
        api.get('/account/usage'),
      ]);

      setUserProfile(profileResponse.data);

      const dashboard = dashboardResponse.data || {};
      if (dashboard.pricing) {
        setOrgInfo({
          company: dashboard.pricing.company || '',
          phone: dashboard.pricing.phone || '',
          location: dashboard.pricing.location || '',
        });
        setPricingData(dashboard.pricing);
      }

      if (dashboard.summary) {
        setDataUsage({
          storageUsed: dashboard.summary.storage_used_gb || 0,
          storageLimit: dashboard.summary.storage_limit_gb || 0,
          documentsUsed: dashboard.summary.documents_uploaded || 0,
          documentsLimit: dashboard.limits?.max_documents_per_project || 0,
          projectsUsed: dashboard.summary.projects_created || 0,
          projectsLimit: dashboard.summary.projects_limit || 0,
        });
      }

      const tokens = accountResponse.data?.tokens || {};
      setTokenUsage({
        dailyUsed: tokens.daily_used || 0,
        dailyLimit: tokens.daily_limit || 0,
        weeklyUsed: tokens.weekly_used || 0,
        weeklyLimit: tokens.weekly_limit || 0,
        todayUsage: tokens.today_usage || 0,
        weekUsage: tokens.week_usage || 0,
        monthUsage: tokens.month_usage || 0,
      });
    } catch (error) {
      console.error('Failed to load user data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setLoading(false);
      setProjectsLoaded(false);
      return;
    }
    loadUserData();
  }, [isLoaded, isSignedIn, loadUserData]);

  const refresh = useCallback(() => {
    if (!isSignedIn) return Promise.resolve();
    return loadUserData();
  }, [isSignedIn, loadUserData]);

  // Memoize context value so consumers don't re-render on unrelated parent updates.
  const value = useMemo(() => ({
    loading: loading || !projectsLoaded,
    projectsLoaded,
    setProjectsLoaded,
    userProfile,
    orgInfo,
    pricingData,
    dataUsage,
    tokenUsage,
    refresh,
  }), [loading, projectsLoaded, userProfile, orgInfo, pricingData, dataUsage, tokenUsage, refresh]);

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};
